import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Input,
  Button,
  Upload,
  Select,
  InputNumber,
  message,
  Spin,
  Card,
  Space,
  Row,
  Col,
  Divider,
  Switch,
  Alert,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import TinyEditor from "../../components/Editor";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import { SEO_CONFIG } from "../../seo-congig";

const { TextArea } = Input;

const AddBrand = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editorContent, setEditorContent] = useState("");
  const [countries, setCountries] = useState([]);
  const [fetchingCountries, setFetchingCountries] = useState(false);
  // Add states for validation errors
  const [validationErrors, setValidationErrors] = useState({});
  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  useEffect(() => {
    const fetchCountries = async () => {
      setFetchingCountries(true);
      try {
        const response = await api.get("/panel/country?per_page=all");
        setCountries(response.data.data);
      } catch (error) {
        message.error("خطا در دریافت لیست کشورها");
      } finally {
        setFetchingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  const handleEditorChange = (content) => {
    setEditorContent(content);
  };

  // Function to handle validation errors
  const handleValidationErrors = (error) => {
    // Clear previous validation errors
    setValidationErrors({});
    setHasValidationErrors(false);
    
    // Check if it's a validation error (status 422)
    if (error.response && error.response.status === 422) {
      const errorData = error.response.data;
      if (errorData && errorData.data && errorData.data.errors) {
        const errors = errorData.data.errors;
        
        // Set state for validation errors
        setValidationErrors(errors);
        setHasValidationErrors(true);
        
        // Set form field errors
        form.setFields(
          Object.keys(errors).map(field => ({
            name: field,
            errors: errors[field]
          }))
        );
        
        message.error("لطفاً خطاهای اعتبارسنجی را برطرف کنید");
        return true;
      }
    }
    
    // Handle other types of errors
    message.error("خطا در ذخیره اطلاعات");
    return false;
  };

  // Function to render validation error messages
  const renderValidationErrors = () => {
    if (!hasValidationErrors) return null;
    
    return (
      <Alert
        message="خطاهای اعتبارسنجی"
        description={
          <ul className="mt-2 list-disc pr-5">
            {Object.keys(validationErrors).map((field) => (
              validationErrors[field].map((error, index) => (
                <li key={`${field}-${index}`}>{error}</li>
              ))
            ))}
          </ul>
        }
        type="error"
        showIcon
        className="mb-4"
      />
    );
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();

      const appendIfExists = (key, value) => {
        if (value !== undefined && value !== null && value !== "") {
          formData.append(key, value);
        }
      };

      // Handle multiple country selection
      if (values.countries && values.countries.length > 0) {
        // For each selected country, append a countries[] array element
        values.countries.forEach(countryId => {
          formData.append("countries[]", countryId);
        });
      }

      formData.append("name", values.name);
      formData.append("slug", values.slug);
      formData.append("body", editorContent);
      formData.append("type", values.type);

      // SEO Fields
      appendIfExists("seo_title", values.seo_title);
      appendIfExists("seo_description", values.seo_description);
      appendIfExists("canonical", values.canonical);
      formData.append("follow", values.follow ? "1" : "0");
      formData.append("index", values.index ? "1" : "0");

      if (values.banner?.file) {
        formData.append("image", values.banner.file);
      }

      const response = await api.post("/panel/brand", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      message.success("برند با موفقیت اضافه شد.");
      navigate("/brands");
    } catch (error) {
      // Use the validation error handler
      handleValidationErrors(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <div>
        <h2 className="mb-10 text-xl">افزودن برند</h2>
        <Spin spinning={loading}>
          {/* Display validation errors at the top of the form */}
          {renderValidationErrors()}
          
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="عنوان برند"
                  rules={[
                    {
                      required: true,
                      message: "لطفاً عنوان را وارد کنید.",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={12}>
                {/* Changed field name from "country" to "countries" for clarity */}
                <Form.Item 
                  name="countries" 
                  label="کشورها"
                  // tooltip="می‌توانید چندین کشور را انتخاب کنید"
                >
                  <Select
                    mode="multiple"  // Enable multiple selection
                    allowClear
                    placeholder="کشورها را انتخاب کنید"
                    loading={fetchingCountries}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {countries.map((country) => (
                      <Select.Option key={country.id} value={country.id}>
                        {country.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="slug"
                  label="پیوند(لینک این برند در وبسایت)"
                  rules={[
                    {
                      required: true,
                      message: "لطفاً پیوند را وارد کنید.",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="type"
                  label="نوع  "
                  rules={[
                    {
                      required: true,
                      message: "لطفاً نوع  برند را انتخاب کنید.",
                    },
                  ]}
                >
                  <Select placeholder="نوع  برند را انتخاب کنید">
                    <Select.Option value="car">خودرو</Select.Option>
                    <Select.Option value="part">قطعه</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="محتوا"
              required
              validateTrigger={["onChange", "onBlur"]}
              rules={[
                {
                  required: true,
                  validator: (_, value) => {
                    if (!editorContent) {
                      return Promise.reject("لطفاً محتوا را وارد کنید.");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <TinyEditor
                content={editorContent}
                onEditorChange={handleEditorChange}
                model={"Category"}
                height={300}
              />
            </Form.Item>

            <div className="border p-4 rounded-lg mb-4">
              <h3 className="text-xl text-blue-500 text-center mb-4">Media</h3>

              <Form.Item
                name="banner"
                label="تصویر بنر"
                rules={[
                  {
                    required: true,
                    message: "لطفاً تصویر بنر را آپلود کنید.",
                  },
                ]}
              >
                <Upload
                  maxCount={1}
                  beforeUpload={() => false}
                  listType="picture"
                >
                  <Button icon={<UploadOutlined />} loading={uploading}>
                    آپلود تصویر
                  </Button>
                </Upload>
              </Form.Item>
            </div>

            <Divider>بخش سئو</Divider>
            <Form.Item
              name="seo_title"
              label="عنوان سئو"
              help={SEO_CONFIG.title.helpText}
            >
              <Input maxLength={SEO_CONFIG.title.maxLength} showCount />
            </Form.Item>

            <Form.Item
              name="seo_description"
              label="توضیحات سئو"
              help={SEO_CONFIG.description.helpText}
            >
              <TextArea
                rows={4}
                maxLength={SEO_CONFIG.description.maxLength}
                showCount
              />
            </Form.Item>

            <Form.Item name="canonical" label="URL Canonical">
              <Input />
            </Form.Item>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item name="follow" label="Follow" valuePropName="checked">
                  <Switch defaultChecked />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="index" label="Index" valuePropName="checked">
                  <Switch defaultChecked />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  ذخیره
                </Button>
                <Button
                  onClick={() => navigate("/brands")}
                  style={{ marginRight: "8px" }}
                >
                  انصراف
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </div>
    </Card>
  );
};

export default AddBrand;